'use client';

import { cn } from '@ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * Card surface.
 *
 * `default` is the stock shadcn look (slate palette) and is what every existing
 * call site gets — it is unchanged. `kor` is the same structure drawn with KOR
 * design tokens instead, so a card matches the rest of the product.
 *
 * The variant flows to CardTitle / CardDescription through context, so you set it
 * once on the Card rather than repeating it on each part.
 *
 * @see Storybook `UI/Card` — pnpm storybook
 */
const cardVariants = cva('', {
  variants: {
    variant: {
      default:
        'rounded-xl border border-slate-200 bg-white text-slate-950 shadow dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50',
      kor: 'rounded-md border border-border bg-white text-title shadow-sm',
    },
  },
  defaultVariants: { variant: 'default' },
});

type CardVariant = NonNullable<VariantProps<typeof cardVariants>['variant']>;

const CardVariantContext = React.createContext<CardVariant>('default');

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <CardVariantContext.Provider value={variant ?? 'default'}>
      <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
    </CardVariantContext.Provider>
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const cardTitleVariants = cva('font-semibold leading-none tracking-tight', {
  variants: {
    variant: { default: '', kor: 'text-title' },
  },
  defaultVariants: { variant: 'default' },
});

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(CardVariantContext);
    return <h3 ref={ref} className={cn(cardTitleVariants({ variant }), className)} {...props} />;
  },
);
CardTitle.displayName = 'CardTitle';

const cardDescriptionVariants = cva('text-sm', {
  variants: {
    variant: {
      default: 'text-slate-500 dark:text-slate-400',
      kor: 'text-note',
    },
  },
  defaultVariants: { variant: 'default' },
});

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(CardVariantContext);
  return <p ref={ref} className={cn(cardDescriptionVariants({ variant }), className)} {...props} />;
});
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
